import React from 'react';
import {ReportKpi} from '@app/services/reports';

/**
 * Headline figures for any report.
 *
 * Formatting is driven by the `format` the API sends, not by the component
 * guessing from the key name — so a new report gets correct currency and
 * percent rendering without touching this file.
 */

/**
 * Neutral by default.
 *
 * A positional rotation that included 'danger' painted the fourth tile red
 * regardless of meaning — on Materials Revenue that was "Auto-generated", which
 * is not a problem, just a number. Colour here carries no judgement unless a
 * report explicitly asks for one via `tone`.
 */
const NEUTRAL = ['primary', 'info', 'secondary', 'primary'];

export const formatKpi = (value: number, format: string): string => {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  }
  if (format === 'percent') {
    return `${(value || 0).toFixed(1)}%`;
  }
  return new Intl.NumberFormat('en-GB').format(value || 0);
};

interface Props {
  kpis: ReportKpi[];
  loading?: boolean;
}

const KpiStrip = ({kpis, loading}: Props) => {
  if (loading) {
    return (
      <div className="row">
        {[0, 1, 2, 3].map((i) => (
          <div className="col-lg-3 col-6 d-flex" key={i}>
            <div className="small-box w-100 bg-light">
              <div className="inner">
                <h3 className="text-muted">&nbsp;</h3>
                <p className="text-muted">Loading</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!kpis || !kpis.length) return null;

  return (
    <div className="row">
      {kpis.map((k, i) => (
        <div className="col-lg-3 col-6 d-flex" key={k.key}>
          <div className={`small-box w-100 bg-${(k as any).tone || NEUTRAL[i % NEUTRAL.length]}`}>
            <div className="inner">
              <h3 style={{fontSize: '1.8rem'}}>{formatKpi(k.value, k.format)}</h3>
              <p className="mb-0">{k.label}</p>
              {k.detail && (
                <small style={{opacity: 0.85}}>{k.detail}</small>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default KpiStrip;
